import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCopy, FiCheck, FiHeart, FiMessageCircle, FiShare } = FiIcons;

const CaptionGenerator = ({ prompt, data }) => {
  const [copiedIndex, setCopiedIndex] = useState(null);

  const captions = [
    {
      type: "Inspirational",
      text: `Just touched down in paradise — ${data.experience || 'creating unforgettable memories'}. ✨\n\nThere's something magical about ${data.location || 'travel'} that speaks to the soul. As a ${data.gender?.toLowerCase() || 'melanated'} traveler, I'm here to show you that every destination is meant for us too. 🌍\n\n${data.vibe || 'Luxe travel'} isn't just a hashtag — it's a lifestyle. From ${data.skinTone?.toLowerCase() || 'beautiful'} skin glowing in golden hour to ${data.hairType?.toLowerCase() || 'natural'} hair flowing in ocean breeze, representation matters in every frame.\n\n#MelanatedTravel #${data.location?.replace(/ /g, '') || 'Paradise'} #TravelWhileBlack #InclusiveTravel #BlackTravelFeed`,
      engagement: "High emotional connection, storytelling focus"
    },
    {
      type: "Affiliate CTA",
      text: `POV: You're living your best life at ${data.location || 'your dream destination'} 🏝️\n\nThis ${data.experience?.toLowerCase() || 'moment'} hit different when you're prepared with the right gear! My travel essentials are linked in bio — from accessibility aids to skincare for melanated skin that travels well.\n\n✨ Use code MELANATED20 for 20% off your first order\n🔗 Link in bio for instant access\n\nWhat's your must-have travel essential? Drop it below! 👇\n\n#MelanatedTravel #TravelEssentials #Ad #TravelGear #${data.location?.replace(/ /g, '') || 'Paradise'}`,
      engagement: "Direct sales focus with community engagement"
    },
    {
      type: "Community Building",
      text: `Melanated travelers, this one's for us! 💫\n\n${data.location || 'This destination'} reminded me why representation in travel matters. Every ${data.gender?.toLowerCase() || 'person'} deserves to see themselves in wanderlust content.\n\n${data.mobility !== 'None' ? `Shoutout to accessible travel options that made this ${data.experience?.toLowerCase() || 'experience'} possible! 🙌` : `The ${data.vibe?.toLowerCase() || 'energy'} here is unmatched!`}\n\nTag a friend who needs to see this! Let's normalize melanated faces in luxury travel spaces. 🌟\n\nWhere should we explore next? 🗺️\n\n#MelanatedTravel #BlackTravelMovement #TravelCommunity #${data.location?.replace(/ /g, '') || 'Wanderlust'}`,
      engagement: "Community focus, user-generated content potential"
    }
  ];

  const copyCaption = async (index, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gray-800 rounded-2xl p-6 shadow-xl">
        <h2 className="text-3xl font-bold mb-6 text-center text-yellow-400">
          Social Media Caption Generator
        </h2>

        <div className="space-y-6">
          {captions.map((caption, index) => (
            <div key={index} className="bg-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">{caption.type}</h3>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-400">{caption.engagement}</span>
                  <button
                    onClick={() => copyCaption(index, caption.text)}
                    className="bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
                  >
                    <SafeIcon icon={copiedIndex === index ? FiCheck : FiCopy} />
                    <span>{copiedIndex === index ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-4 mb-4">
                <p className="text-gray-100 whitespace-pre-line leading-relaxed">
                  {caption.text}
                </p>
              </div>

              <div className="flex items-center space-x-4 text-gray-400">
                <div className="flex items-center space-x-1">
                  <SafeIcon icon={FiHeart} />
                  <span className="text-sm">Est. 2.5k likes</span>
                </div>
                <div className="flex items-center space-x-1">
                  <SafeIcon icon={FiMessageCircle} />
                  <span className="text-sm">150 comments</span>
                </div>
                <div className="flex items-center space-x-1">
                  <SafeIcon icon={FiShare} />
                  <span className="text-sm">85 shares</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-gray-700 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Hashtag Strategy</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Trending Tags</h4>
              <div className="flex flex-wrap gap-2">
                {['#MelanatedTravel', '#BlackTravelFeed', '#TravelWhileBlack', '#InclusiveTravel'].map(tag => (
                  <span key={tag} className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Location Tags</h4>
              <div className="flex flex-wrap gap-2">
                {[`#${data.location?.replace(/ /g, '') || 'Paradise'}`, '#Caribbean', '#LuxuryTravel', '#Wanderlust'].map(tag => (
                  <span key={tag} className="bg-green-600 text-white px-3 py-1 rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaptionGenerator;